import { useState } from 'react';

import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { App, Card, DatePicker, Flex, Input, Select, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';

import { ReactComponent as SearchIcon } from '../../../assets/magnifier.svg';
import Button from '../../../custom/button/button';
import { useDebounce } from '../../../hooks/useDebounce';
import { useDownload } from '../../../hooks/useDownload';
import { usePagination } from '../../../hooks/usePagination';
import {
  getAllAcademicSession,
  getAllApplicationBatch,
  getAllModeOfStudy,
  getAllPrograms,
  getAllProgramType,
} from '../../../requests';
import api from '../../../utils/api';
import { buildQuery } from '../../../utils/buildQuery';
import { formatFullDate } from '../../../utils/formatDate';
import styles from '../../setup/styles.module.scss';

const { RangePicker } = DatePicker;

interface ApplicantRecord {
  applicantId: string;
  applicationNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string;
  phoneNumber: string;
  genderName: string;
  programName: string;
  programTypeName: string;
  modeOfStudyName: string;
  applicationBatchName: string;
  sessionName: string;
  countryName: string;
  stateName: string;
  createdDate: string;
  isAdmitted: string | null;
  isSubmitted: boolean;
  hasPaidApplicationFee: boolean | null;
  hasPaidAcceptanceFee: boolean;
  hasAcceptedAdmission: boolean | null;
  matricNumber: string | null;
}

interface ApplicantFilters {
  ApplicationType: number | '';
  ModeOfStudyId: number | '';
  ProgramId: number | '';
  ProgramTypeId: number | '';
  ApplicationBatchId: number | '';
  SessionId: number | '';
  Name: string;
  Email: string;
  ApplicationNumber: string;
  CreatedFrom: string;
  CreatedTo: string;
}

const APPLICATION_TYPE_OPTIONS = [
  { label: 'All', value: 0 },
  { label: 'Incomplete', value: 1 },
  { label: 'Completed', value: 2 },
  { label: 'Admitted', value: 3 },
  { label: 'Accepted Admission', value: 4 },
];

const STUDY_MODE_OPTIONS = [
  { label: 'Full-Time', value: 1 },
  { label: 'Part-Time', value: 2 },
  { label: 'Online', value: 4 },
];

const EMPTY_FILTERS: ApplicantFilters = {
  ApplicationType: '',
  ModeOfStudyId: '',
  ProgramId: '',
  ProgramTypeId: '',
  ApplicationBatchId: '',
  SessionId: '',
  Name: '',
  Email: '',
  ApplicationNumber: '',
  CreatedFrom: '',
  CreatedTo: '',
};

const getApplicantsFilter = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (await api.get(`/Authorization/GetApplicantsFilter${url}`))?.data;
};

const downloadApplicantsFilter = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (
    await api.get(`/Authorization/DownloadApplicantsFilter${url}`, { responseType: 'blob' })
  )?.data as Blob;
};

const parseBlobError = async (error: any): Promise<string> => {
  const fallback = 'Unable to download the applicant report. Please try again.';
  try {
    const blob: Blob = error?.response?.data;
    if (blob instanceof Blob) {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json?.message || fallback;
    }
  } catch {}
  return error?.response?.data?.message || fallback;
};

const ApplicantReport = () => {
  const { notification } = App.useApp();
  const { currentPage, onChange } = usePagination();
  const { downloadExcelFile } = useDownload();

  const [filters, setFilters] = useState<ApplicantFilters>(EMPTY_FILTERS);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const debouncedName = useDebounce(filters.Name, 500);
  const debouncedEmail = useDebounce(filters.Email, 500);
  const debouncedAppNumber = useDebounce(filters.ApplicationNumber, 500);

  const buildParams = () => ({
    PageNumber: currentPage,
    PageSize: 10,
    ApplicationType: filters.ApplicationType !== '' ? filters.ApplicationType : undefined,
    ModeOfStudyId: filters.ModeOfStudyId || undefined,
    ProgramId: filters.ProgramId || undefined,
    ProgramTypeId: filters.ProgramTypeId || undefined,
    ApplicationBatchId: filters.ApplicationBatchId || undefined,
    SessionId: filters.SessionId || undefined,
    Name: debouncedName || undefined,
    Email: debouncedEmail || undefined,
    ApplicationNumber: debouncedAppNumber || undefined,
    CreatedFrom: filters.CreatedFrom || undefined,
    CreatedTo: filters.CreatedTo || undefined,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['applicant-report', buildParams()],
    queryFn: () => getApplicantsFilter(buildParams()),
  });

  const [programQuery, programTypeQuery, batchQuery, sessionQuery, modeOfStudyQuery] = useQueries({
    queries: [
      { queryKey: ['programs'], queryFn: () => getAllPrograms() },
      { queryKey: ['program-types'], queryFn: getAllProgramType },
      { queryKey: ['application-batches'], queryFn: getAllApplicationBatch },
      { queryKey: ['sessions'], queryFn: () => getAllAcademicSession() },
      { queryKey: ['mode-of-study'], queryFn: getAllModeOfStudy },
    ],
  });

  const downloadMutation = useMutation({ mutationFn: downloadApplicantsFilter });

  const records: ApplicantRecord[] = data?.data?.data ?? [];
  const totalRecords: number = data?.data?.totalRecords ?? 0;

  const setFilter = (key: keyof ApplicantFilters, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);
    if (dates?.[0] && dates?.[1]) {
      setFilter('CreatedFrom', dates[0].toISOString());
      setFilter('CreatedTo', dates[1].toISOString());
    } else {
      setFilter('CreatedFrom', '');
      setFilter('CreatedTo', '');
    }
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setDateRange(null);
  };

  const handleDownload = async (downloadAll: boolean) => {
    const activeFilters = {
      ApplicationType: filters.ApplicationType !== '' ? filters.ApplicationType : undefined,
      ModeOfStudyId: filters.ModeOfStudyId || undefined,
      ProgramId: filters.ProgramId || undefined,
      ProgramTypeId: filters.ProgramTypeId || undefined,
      ApplicationBatchId: filters.ApplicationBatchId || undefined,
      SessionId: filters.SessionId || undefined,
      Name: debouncedName || undefined,
      Email: debouncedEmail || undefined,
      ApplicationNumber: debouncedAppNumber || undefined,
      CreatedFrom: filters.CreatedFrom || undefined,
      CreatedTo: filters.CreatedTo || undefined,
    };

    const params = {
      PageNumber: 1,
      PageSize: 100000,
      ...(downloadAll ? {} : activeFilters),
    };

    await downloadMutation.mutateAsync(params, {
      onSuccess: (blob) => {
        downloadExcelFile(
          blob,
          downloadAll ? 'Applicant_Report_All.xlsx' : 'Applicant_Report_Filtered.xlsx',
        );
      },
      onError: async (error: any) => {
        const description = await parseBlobError(error);
        notification.error({ message: 'Download Failed', description });
      },
    });
  };

  const programOptions = (programQuery.data?.data as any[])?.map((p) => ({
    label: p.name,
    value: p.id,
  }));
  const programTypeOptions = (programTypeQuery.data?.data as any[])?.map((p) => ({
    label: p.name,
    value: p.id,
  }));
  const batchOptions = (batchQuery.data?.data as any[])?.map((b) => ({
    label: b.batchName,
    value: b.id,
  }));
  const sessionOptions = (sessionQuery.data?.data as any[])?.map((s) => ({
    label: s.name,
    value: s.id,
  }));
  const modeOptions = (modeOfStudyQuery.data?.data as any[])?.map((m) => ({
    label: m.name,
    value: m.id,
  })) ?? STUDY_MODE_OPTIONS;

  const columns: ColumnsType<ApplicantRecord> = [
    {
      key: 'sn',
      title: 'S/N',
      width: 55,
      render: (_, __, index) => (currentPage - 1) * 10 + index + 1,
    },
    {
      key: 'applicationNumber',
      title: 'App. No.',
      dataIndex: 'applicationNumber',
    },
    {
      key: 'name',
      title: 'Full Name',
      render: (_, r) =>
        `${r.firstName}${r.middleName ? ` ${r.middleName}` : ''} ${r.lastName}`,
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
    },
    {
      key: 'phoneNumber',
      title: 'Phone',
      dataIndex: 'phoneNumber',
    },
    {
      key: 'programName',
      title: 'Program',
      dataIndex: 'programName',
    },
    {
      key: 'programTypeName',
      title: 'Type',
      dataIndex: 'programTypeName',
    },
    {
      key: 'modeOfStudyName',
      title: 'Mode',
      dataIndex: 'modeOfStudyName',
    },
    {
      key: 'sessionName',
      title: 'Session',
      dataIndex: 'sessionName',
    },
    {
      key: 'applicationBatchName',
      title: 'Batch',
      dataIndex: 'applicationBatchName',
    },
    {
      key: 'isSubmitted',
      title: 'Submitted',
      dataIndex: 'isSubmitted',
      render: (val: boolean) => (
        <Tag color={val ? 'success' : 'default'}>{val ? 'Yes' : 'No'}</Tag>
      ),
    },
        {
        key: 'isAdmitted',
        title: 'Admitted',
        dataIndex: 'isAdmitted',
        render: (val: string | null) => {
            if (!val) {
            return <Tag color="default">Pending</Tag>;
            }

            return (
            <Tag
                color={val.toLowerCase() === 'accept' ? 'success' : 'error'}
            >
                {val.toLowerCase() === 'accept' ? 'Yes' : 'No'}
            </Tag>
            );
        },
        },
    {
      key: 'hasPaidApplicationFee',
      title: 'App. Fee',
      render: (_, r) => (
        <Tag color={r.hasPaidApplicationFee ? 'success' : 'warning'}>
          {r.hasPaidApplicationFee ? 'Paid' : 'Unpaid'}
        </Tag>
      ),
    },
    {
      key: 'hasPaidAcceptanceFee',
      title: 'Acc. Fee',
      render: (_, r) => (
        <Tag color={r.hasPaidAcceptanceFee ? 'success' : 'warning'}>
          {r.hasPaidAcceptanceFee ? 'Paid' : 'Unpaid'}
        </Tag>
      ),
    },
    {
      key: 'createdDate',
      title: 'Date Applied',
      dataIndex: 'createdDate',
      render: (val: string) => formatFullDate(val),
    },
  ];

  return (
    <div>
      <section className="space-between">
        <h3>All Application Report</h3>
        <Flex gap="8px" wrap="wrap">
          <Button
            text="Download Filtered Application"
            onClick={() => handleDownload(false)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
          />
          <Button
            text="Download All"
            onClick={() => handleDownload(true)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
            variant="text"
          />
        </Flex>
      </section>

      <section className={styles.card}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>Filters</p>
          <Flex gap="8px" wrap="wrap" align="center">
            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              placeholder={['Created From', 'Created To']}
              format="DD/MM/YYYY"
              style={{ minWidth: 220, borderColor:'#8F0024' }}
            />
            <Input
              placeholder="Application Number"
              value={filters.ApplicationNumber}
              onChange={(e) => setFilter('ApplicationNumber', e.target.value)}
              prefix={<SearchIcon style={{ width: 14, height: 14 }} />}
              style={{ width: 180, borderColor:'#8F0024' }}
              allowClear
            />
            <Input
              placeholder="Name"
              value={filters.Name}
              onChange={(e) => setFilter('Name', e.target.value)}
              style={{ width: 150, borderColor:'#8F0024' }}
              allowClear
            />
            <Input
              placeholder="Email"
              value={filters.Email}
              onChange={(e) => setFilter('Email', e.target.value)}
              style={{ width: 180, borderColor:'#8F0024' }}
              allowClear
            />
            <Select
              placeholder="Application Type"
              value={filters.ApplicationType !== '' ? filters.ApplicationType : undefined}
              allowClear
              options={APPLICATION_TYPE_OPTIONS}
              style={{ width: 170, borderColor:'#8F0024' }}
              onChange={(value) => setFilter('ApplicationType', value ?? '')}
            />
            <Select
              placeholder="Mode of Study"
              value={filters.ModeOfStudyId || undefined}
              allowClear
              options={modeOptions}
              loading={modeOfStudyQuery.isLoading}
              style={{ width: 150, border:'2px black' }}
              onChange={(value) => setFilter('ModeOfStudyId', value ?? '')}
            />
            <Select
              placeholder="Program"
              value={filters.ProgramId || undefined}
              allowClear
              options={programOptions}
              loading={programQuery.isLoading}
              style={{ width: 180, borderColor:'#8F0024' }}
              showSearch
              optionFilterProp="label"
              onChange={(value) => setFilter('ProgramId', value ?? '')}
            />
            <Select
              placeholder="Program Type"
              value={filters.ProgramTypeId || undefined}
              allowClear
              options={programTypeOptions}
              loading={programTypeQuery.isLoading}
              style={{ width: 150, borderColor:'#8F0024' }}
              onChange={(value) => setFilter('ProgramTypeId', value ?? '')}
            />
            <Select
              placeholder="Batch"
              value={filters.ApplicationBatchId || undefined}
              allowClear
              options={batchOptions}
              loading={batchQuery.isLoading}
              style={{ width: 140, borderColor:'#8F0024' }}
              onChange={(value) => setFilter('ApplicationBatchId', value ?? '')}
            />
            <Select
              placeholder="Session"
              value={filters.SessionId || undefined}
              allowClear
              options={sessionOptions}
              loading={sessionQuery.isLoading}
              style={{ width: 140, borderColor:'#8F0024' }}
              onChange={(value) => setFilter('SessionId', value ?? '')}
            />
            <Button
              text="Clear Filters"
              variant="text"
              onClick={handleClearFilters}
              type="button"
            />
          </Flex>
        </div>

        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            Showing <strong>{records.length}</strong> of <strong>{totalRecords}</strong> records
          </p>
        </Flex>

        <Table
          dataSource={records.map((r, i) => ({ ...r, key: `${r.applicantId}-${i}` }))}
          columns={columns}
          loading={isLoading}
          scroll={{ x: true }}
          pagination={{
            position: ['bottomCenter'],
            current: currentPage,
            total: totalRecords,
            onChange,
            pageSize: 10,
            showSizeChanger: false,
          }}
        />
      </section>
    </div>
  );
};

export default ApplicantReport;
